<template>
  <div class="root" ref="playerContainer">
    <div v-if="showYoutube && !youTubeList" style="position: relative; height: 70%">
      <YoutubeFrame
        :videoUrl="videoUrlForShow"
        style="height: 100%"
        @videoEnded="handleVideoEnded"
        @playlistEmbedFailed="handlePlaylistEmbedFailed"
      />
      <!-- Playlist navigation buttons -->
      <div v-if="currentPlaylist && currentPlaylist.type === 'playlist'" style="position: absolute; top: 10px; right: 10px; z-index: 1000; display: flex; gap: 10px;">
        <button @click="playPreviousVideo" 
                :disabled="currentPlaylistIndex === 0"
                style="background: rgba(0,0,0,0.7); color: white; border: none; padding: 8px 12px; border-radius: 8px; cursor: pointer; font-size: 14px;"
                :style="{ opacity: currentPlaylistIndex === 0 ? 0.5 : 1 }">
          ← Prev
        </button>
        <button @click="playNextVideo"
                :disabled="currentPlaylistIndex >= currentPlaylist.videoCount - 1"
                style="background: rgba(0,0,0,0.7); color: white; border: none; padding: 8px 12px; border-radius: 8px; cursor: pointer; font-size: 14px;"
                :style="{ opacity: currentPlaylistIndex >= currentPlaylist.videoCount - 1 ? 0.5 : 1 }">
          Next →
        </button>
      </div>
    </div>
    <CanvasChart
      v-if="chart && !showYoutube && !showChartInBottomBar"
      :segments="history.segments"
      :bpm="history.bpm"
      :rpm="history.rpm"
      :ftp="history.ftp"
      :thresholdHR="user.thresholdHR"
      :watts="history.watts"
      :showWattsDiagram="true"
    />
    <div class="pause-label" v-if="isPause">Pause</div>
    <VideosList v-if="youTubeList" @videoUrl="getVideoUrlForShow" @playlistData="handlePlaylistData" />
    <section class="bottom-section">
      <RouteMapBlock ref="routeMapBlock" />
      <BottomBar
        class="bottom-bar"
        :isFullscreen="isFullscreen"
        @onToggleFullscreen="toggleFullscreen"
      />
    </section>
  </div>
</template>

<script>
  import { SegmentSoundEffectType } from '@trainerday/ergdb-types'
  import configuration from './configuration'
  import BottomBar from '@/components/BottomBar'
  import YoutubeFrame from '@/components/YoutubeFrame'
  import VideosList from '@/components/VideosList'
  import RouteMapBlock from '@/components/RouteMapBlock'
  import { mapActions, mapState } from 'vuex'
  import CanvasChart from '@/components/CanvasChart'
  const { websocketsUrl } = configuration
  const { io } = require('socket.io-client')

  const delay = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds))

  export default {
    name: 'RealTimeClient',
    components: {
      CanvasChart,
      BottomBar,
      YoutubeFrame,
      VideosList,
      RouteMapBlock
    },
    async mounted() {
      // Check for BLE mode from query string
      const bleMode = this.$route.query.ble === 'yes'
      this.$store.dispatch('setBleMode', bleMode)
      
      if (this.user.token) {
        await this.$store.dispatch('checkUserId')
        await Promise.all([this.$store.dispatch('getVideoList'), this.$store.dispatch('getRoute')])
      } else {
        await this.$store.dispatch('setHasLoaded')
      }
      document.addEventListener('fullscreenchange', this.onFullscreenChange)
      
      // Track page load and identify user with Clicky
      if (typeof clicky !== 'undefined') {
        // Track page load
        clicky.log('page_load', 'Web Trainer Loaded')
        
        // Identify user if logged in
        if (this.$store.state.user.userId) {
          const userId = this.$store.state.user.userId
          const userName = this.$store.state.user.userName || `User ${userId}`
          
          clicky.log('user_identified', 'User Identified', {
            user_id: userId,
            user_name: userName
          })
        }
      }
    },
    data() {
      return {
        activity: [],
        second: 0,
        target: 0,
        interval: '00:00',
        total: '00:00',
        videoUrlForShow: '',
        isFullscreen: false,
        currentPlaylist: null,
        currentPlaylistIndex: 0,
        soundEffects: {
          // used in older app builds
          beep3: new Audio('/beep_for_three.mp3'),
          beepUp: new Audio('/beep_up_v2.mp3'),
          beepDown: new Audio('/beep_down_v2.mp3'),

          // new app builds
          beepLong: new Audio('/beep_long.mp3'),
          beepShort: new Audio('/beep_short.mp3')
        }
      }
    },
    watch: {
      'current.soundEffect'(soundEffect) {
        // playSound is async: a try/catch here never saw the browser's autoplay
        // block (NotAllowedError until the rider has clicked the page), so it went
        // to Bugsink as an unhandled rejection. A beep that can't play is skipped.
        this.playSound(soundEffect).catch(() => {})
      },

      statusActivity(status) {
        if (status === 'ready') {
          this.$store.commit('clearHistory')
          this.$store.commit('clearCurrent')
        }
        
        // Track workout start with Clicky
        if (status === 'playing') {
          if (typeof clicky !== 'undefined') {
            const userId = this.$store.state.user.userId
            const sportType = this.$store.state.current.sportType
            const ftp = this.$store.state.current.ftp
            
            clicky.log('workout_started', 'Workout Started', {
              user_id: userId,
              sport_type: sportType,
              ftp: ftp
            })
          }
        }
      },

      $screenHeight() {
        this.onPageResize()
      }
    },
    computed: {
      ...mapState(['user', 'history', 'current', 'userRoute']),
      isLogged() {
        return this.user.isLogged
      },
      isPause() {
        return (this.history.watts.length && !this.current.currentSecond) || this.statusActivity === 'pause'
      },
      chart() {
        return this.user.chart
      },
      showYoutube() {
        return this.user.youTubeFrame
      },
      showChartInBottomBar() {
        return this.user.chartInBottomBar
      },
      youTubeList() {
        return this.user.youTubeList
      },
      statusActivity() {
        return this.current.statusActivity
      }
    },
    created() {
      window.addEventListener('beforeunload', this.beforeWindowUnload)
      this.onPageResize()
      this.init()
      setInterval(async () => {
        try {
          const screenLock = await navigator.wakeLock.request('screen')
          await screenLock.release()
        } catch (e) {}
      }, 5000)
    },
    beforeDestroy() {
      document.removeEventListener('fullscreenchange', this.onFullscreenChange)
      window.removeEventListener('beforeunload', this.beforeWindowUnload)
    },
    methods: {
      ...mapActions(['checkUserId', 'showYoutubeFrame', 'showYoutubeVideo', 'setHasLoaded']),

      onFullscreenChange() {
        // При нажатии ESC
        const isCurrentlyFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement)
        if (!isCurrentlyFullscreen) {
          this.isFullscreen = false
        }
      },

      toggleFullscreen() {
        this.isFullscreen = !this.isFullscreen
        const playerContainer = this.$refs.playerContainer
        if (this.isFullscreen) {
          // Включение полноэкранного режима
          if (playerContainer.requestFullscreen) {
            playerContainer.requestFullscreen()
          } else if (playerContainer.mozRequestFullScreen) { // Firefox
            playerContainer.mozRequestFullScreen()
          } else if (playerContainer.webkitRequestFullscreen) { // Chrome, Safari and Opera
            playerContainer.webkitRequestFullscreen()
          }
        } else {
          if (document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement) {
            // Выход из полноэкранного режима
            if (document.exitFullscreen) {
              document.exitFullscreen()
            } else if (document.mozCancelFullScreen) { // Firefox
              document.mozCancelFullScreen()
            } else if (document.webkitExitFullscreen) { // Chrome, Safari and Opera
              document.webkitExitFullscreen()
            }
          }
        }
      },

      onPageResize() {
        const { chartInBottomBar, youTubeFrame } = this.$store.state.user
        if (this.$screenHeight <= 475 && !chartInBottomBar) {
          this.$store.state.user.chartInBottomBar = true
          this.$store.state.user.youTubeFrame = false
          this.$store.state.user.youTubeList = false
        }

        if (this.$screenHeight > 475 && chartInBottomBar && !youTubeFrame) {
          this.$store.state.user.chartInBottomBar = false
        }
      },

      async playSound(soundEffect) {
        if (soundEffect === SegmentSoundEffectType.SINGLE_BEEP) {
          this.soundEffects.beepLong.volume = 1
          await this.soundEffects.beepLong.play()
        }

        if (soundEffect === SegmentSoundEffectType.SINGLE_BEEP_LOW_VOLUME) {
          this.soundEffects.beepLong.volume = 0.32
          await this.soundEffects.beepLong.play()
        }

        if (soundEffect === SegmentSoundEffectType.SINGLE_BEEP_MID_VOLUME) {
          this.soundEffects.beepLong.volume = 0.56
          await this.soundEffects.beepLong.play()
        }

        if (soundEffect === SegmentSoundEffectType.FOUR_BEEPS_LOW_VOLUME) {
          await this.playFourBeeps(0.32)
        }

        if (soundEffect === SegmentSoundEffectType.FOUR_BEEPS_MID_VOLUME) {
          await this.playFourBeeps(0.56)
        }

        if (soundEffect === SegmentSoundEffectType.FOUR_BEEPS_HIGH_VOLUME) {
          await this.playFourBeeps(1)
        }

        if (soundEffect === SegmentSoundEffectType.FOUR_BEEPS_PROGRESSIVE_VOLUME) {
          await this.playFourBeeps(0.2, true)
        }

        if (soundEffect === SegmentSoundEffectType.UP) {
          await this.soundEffects.beepUp.play()
        }

        if (soundEffect === SegmentSoundEffectType.DOWN) {
          await this.soundEffects.beepDown.play()
        }

        if (soundEffect === SegmentSoundEffectType.BEEPS_DOWN) {
          await this.soundEffects.beep3.play()
          await delay(800)
          await this.soundEffects.beep3.play()
          await delay(800)
          await this.soundEffects.beep3.play()
          await delay(800)
          await this.soundEffects.beepDown.play()
        }

        if (soundEffect === SegmentSoundEffectType.BEEPS_UP) {
          await this.soundEffects.beep3.play()
          await delay(800)
          await this.soundEffects.beep3.play()
          await delay(800)
          await this.soundEffects.beep3.play()
          await delay(800)
          await this.soundEffects.beepUp.play()
        }
      },

      async playFourBeeps(volume = 1, soundRise = false) {
        this.soundEffects.beepShort.volume = volume
        this.soundEffects.beepLong.volume = volume

        await this.soundEffects.beepShort.play()
        await delay(800)
        if (soundRise) {
          this.soundEffects.beepShort.volume += 0.3
        }
        await this.soundEffects.beepShort.play()
        await delay(800)
        if (soundRise) {
          this.soundEffects.beepShort.volume += 0.3
        }
        await this.soundEffects.beepShort.play()
        await delay(800)
        if (soundRise) {
          this.soundEffects.beepLong.volume = 1
        }
        await this.soundEffects.beepLong.play()
      },

      beforeWindowUnload(e) {
        if (this.userRoute.isLoaded && (this.statusActivity === 'playing' || this.statusActivity === 'pause')) {
          const confirmed = window.confirm('Do you really want to leave?')
          if (!confirmed) {
            e.preventDefault()
            e.returnValue = ''
          }
        }
      },

      getVideoUrlForShow(videoUrl) {
        console.log('getVideoUrlForShow called with:', videoUrl)
        console.log('showYoutube before dispatch:', this.showYoutube)
        console.log('youTubeList before dispatch:', this.youTubeList)
        
        this.$store.dispatch('showYoutubeVideo')
        
        console.log('showYoutube after dispatch:', this.showYoutube)
        console.log('youTubeList after dispatch:', this.youTubeList)
        
        // Check if this is from a playlist first
        const playlistInfo = this.checkIfFromPlaylist(videoUrl)
        
        if (playlistInfo.isFromPlaylist) {
          // For now, just play individual videos from playlist without YouTube playlist embed
          // since the playlist might not be publicly accessible for embedding
          console.log('Playing individual video from playlist:', playlistInfo)
          
          // Store playlist info for manual navigation
          this.currentPlaylist = playlistInfo.playlist
          this.currentPlaylistIndex = playlistInfo.videoIndex
          
          // Extract video ID from the individual video URL and play it
          const videoId = this.extractVideoId(videoUrl)
          if (videoId) {
            const timestamp = new Date().getTime()
            this.videoUrlForShow = `https://www.youtube.com/embed/${videoId}?autoplay=1&t=${timestamp}`
            console.log('Set individual video from playlist:', this.videoUrlForShow)
            return
          }
        }
        
        // Handle individual video (not from playlist)
        // Clear playlist state for individual videos
        this.currentPlaylist = null
        this.currentPlaylistIndex = 0
        
        let id
        if (videoUrl.indexOf('youtu.be/') > 0) {
          id = videoUrl.split('/')[3]
        } else {
          id = videoUrl.split('?v=')[1]
          if (id && id.indexOf('&') > 0) {
            id = id.substring(0, id.indexOf('&'))
          }
        }
        
        // Only update if we have a valid video ID
        if (id) {
          // Add cache-busting parameter to force iframe reload
          const timestamp = new Date().getTime()
          this.videoUrlForShow = `https://www.youtube.com/embed/${id}?autoplay=1&t=${timestamp}`
          console.log('Set videoUrlForShow to:', this.videoUrlForShow)
        } else {
          console.error('Could not extract video ID from URL:', videoUrl)
        }
      },

      checkIfFromPlaylist(videoUrl) {
        // Find the playlist that contains this video
        const videoList = this.$store.state.videoList || []
        for (let i = 0; i < videoList.length; i++) {
          const item = videoList[i]
          if (item.type === 'playlist' && item.videos) {
            const videoIndex = item.videos.findIndex(v => v.url === videoUrl)
            if (videoIndex !== -1) {
              console.log('Video found in playlist:', item.title, 'at index:', videoIndex)
              this.currentPlaylist = item
              this.currentPlaylistIndex = videoIndex
              console.log('Auto-set playlist data:', this.currentPlaylist)
              console.log('Auto-set current index:', this.currentPlaylistIndex)
              
              return {
                isFromPlaylist: true,
                playlist: item,
                videoIndex: videoIndex
              }
            }
          }
        }
        
        return {
          isFromPlaylist: false,
          playlist: null,
          videoIndex: -1
        }
      },

      extractPlaylistId(playlistUrl) {
        // Extract playlist ID from various YouTube playlist URL formats
        if (!playlistUrl) return null
        
        console.log('extractPlaylistId input:', playlistUrl)
        
        // Handle direct playlist URLs like: https://www.youtube.com/playlist?list=PLt5Mb3PkHrmziyCEqsZ_i6j3_vnSnVSbl
        if (playlistUrl.includes('playlist?list=')) {
          const match = playlistUrl.match(/playlist\?list=([^&\n?#]+)/)
          console.log('Direct playlist URL match:', match)
          return match ? match[1] : null
        }
        
        // Handle other formats with list parameter
        const match = playlistUrl.match(/[?&]list=([^&]+)/)
        console.log('General list parameter match:', match)
        return match ? match[1] : null
      },

      extractVideoId(videoUrl) {
        // Extract video ID from various YouTube video URL formats
        if (!videoUrl) return null
        
        if (videoUrl.indexOf('youtu.be/') > 0) {
          return videoUrl.split('/')[3]
        } else if (videoUrl.indexOf('youtube.com/watch') > 0) {
          const match = videoUrl.match(/[?&]v=([^&]+)/)
          return match ? match[1] : null
        } else if (videoUrl.indexOf('youtube.com/embed/') > 0) {
          const parts = videoUrl.split('/embed/')
          if (parts.length > 1) {
            const id = parts[1].split('?')[0]
            return id
          }
        }
        
        return null
      },

      handlePlaylistData(playlistData) {
        // Store playlist data for navigation
        console.log('Received playlist data:', playlistData)
        console.log('Playlist video type:', playlistData.video?.type)
        console.log('Playlist videos array:', playlistData.video?.videos)
        this.currentPlaylist = playlistData.video
        this.currentPlaylistIndex = playlistData.currentIndex
        console.log('Set current playlist:', this.currentPlaylist)
        console.log('Set current index:', this.currentPlaylistIndex)
      },

      handleVideoEnded() {
        console.log('Video ended - checking for auto-advance in playlist')
        // Auto-advance to next video in playlist
        if (this.currentPlaylist && this.currentPlaylist.type === 'playlist') {
          this.playNextVideo()
        } else {
          // For individual videos, show the video selector
          console.log('Individual video ended - showing video selector')
          this.$store.dispatch('showYoutubeFrame')
        }
      },

      playNextVideo() {
        if (this.currentPlaylist && this.currentPlaylist.type === 'playlist') {
          const nextIndex = this.currentPlaylistIndex + 1
          if (nextIndex < this.currentPlaylist.videos.length) {
            const nextVideo = this.currentPlaylist.videos[nextIndex]
            this.currentPlaylistIndex = nextIndex
            console.log('Playing next video:', nextVideo.title)
            
            // Extract video ID directly and update URL to prevent iframe recreation
            const videoId = this.extractVideoId(nextVideo.url)
            if (videoId) {
              const timestamp = new Date().getTime()
              this.videoUrlForShow = `https://www.youtube.com/embed/${videoId}?autoplay=1&t=${timestamp}`
              console.log('Direct video URL update:', this.videoUrlForShow)
            }
          } else {
            console.log('End of playlist reached - showing video selector')
            this.$store.dispatch('showYoutubeFrame')
          }
        }
      },

      playPreviousVideo() {
        if (this.currentPlaylist && this.currentPlaylist.type === 'playlist') {
          const prevIndex = this.currentPlaylistIndex - 1
          if (prevIndex >= 0) {
            const prevVideo = this.currentPlaylist.videos[prevIndex]
            this.currentPlaylistIndex = prevIndex
            console.log('Playing previous video:', prevVideo.title)
            
            // Extract video ID directly and update URL to prevent iframe recreation
            const videoId = this.extractVideoId(prevVideo.url)
            if (videoId) {
              const timestamp = new Date().getTime()
              this.videoUrlForShow = `https://www.youtube.com/embed/${videoId}?autoplay=1&t=${timestamp}`
              console.log('Direct video URL update:', this.videoUrlForShow)
            }
          } else {
            console.log('Already at first video')
          }
        }
      },

      handlePlaylistEmbedFailed() {
        console.log('Playlist embed failed - this handler is for future use')
        // This is a placeholder for future fallback functionality
      },

      init() {
        // The server takes the user from the token; no token = nothing to show
        const { token } = this.user
        if (!token) return
        const socket = io(websocketsUrl, { auth: { token } })

        socket.on('connect_error', (err) => {
          console.error('WebSocket connection refused:', err.message)
        })

        socket.on('connect', () => {
          socket.emit('login', { isClient: true })
          socket.emit('getHistory')
        })

        socket.on('data', (data) => {
          try {
            this.$store.commit('setCurrent', data)
            this.$store.commit('updateHistory', {
              bpm: data.bpm,
              rpm: data.rpm,
              ftp: data.ftp,
              watts: data.watts,
              segments: data.workout ? data.workout.segments : []
            })
            const { currentSecond } = this.$store.state.current
            this.second = currentSecond
          } catch (error) {
            console.error('Error processing WebSocket data:', error)
          }
        })

        socket.on('sendHistory', (data) => {
          this.$store.commit('setHistory', data)
        })

        socket.on('statusChanged', (data) => {
          this.$store.state.current.statusActivity = data.statusActivity || data
        })
      }
    }
  }
</script>

<style lang="scss" scoped>
.root {
  height: 100%;
  display: flex;
  flex: 1;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  background: #000000;
  .pause-label {
    font-size: 36px;
    color: #fff;
    position: absolute;
    left: calc(50% - 55px);
    top: 30%;
    animation: 0.8s linear infinite pauseLabel;
    @-webkit-keyframes pauseLabel {
      0% {
        opacity: 1;
      }
      33% {
        opacity: 0.2;
      }
      100% {
        opacity: 1;
      }
    }
  }
  .fullscreen-link {
    position: absolute;
    left: calc(50% - 100px);
    top: 10px;
    z-index: 2;
    display: flex;
    align-items: center;
    color: #fff !important;
    text-decoration: unset;
    .icon {
      margin-left: 5px;
    }
  }
}


.bottom-section {
  height: 30%;
  position: relative;
}
@media (max-height: 700px) {
  .bottom-section {
    height: 40%;
  }
}
@media (max-height: 520px) {
  .bottom-section {
    height: 45%;
  }
}
@media (max-height: 475px) {
  .bottom-section {
    height: 100%;
  }
}
</style>
